import React, { FC, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { OptionsWithInput } from 'shared/components/OptionsWithInput';
import { getWithdrawAmountOptions } from '../../constants';
import { useSelector } from 'react-redux';
import { walletSelector } from 'modules/payments/store/wallet/selectors';
import { ErrorMessage } from 'shared/components/Form/ErrorMessage';
import { SelectInput } from 'shared/components/Form/SelectInput';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { userPaymentAccountsSelector } from 'modules/payments/store/user-payment-accounts/selectors';
import { MinWithdrawAmountTip } from '../MinWithdrawAmountTip';
import { WithdrawAmounts, WithdrawFundsFields } from 'modules/payments/interfaces';

interface WithdrawalFormProps {
  form: UseFormReturn<WithdrawFundsFields>;
  isDisabled: boolean;
  minAmounts: Record<string, number>;
  preselectedAmounts: WithdrawAmounts;
}

export const WithdrawalForm: FC<WithdrawalFormProps> = ({
  form,
  isDisabled,
  minAmounts,
  preselectedAmounts,
}) => {
  const isMultiCurrencyEnabled = MultiCurrencyConfig.isWithdrawalEnabled();

  const {
    control,
    formState: { errors },
    watch,
    trigger,
  } = form;

  const balance = useSelector(walletSelector.balance);
  const exchangeRate = useSelector(walletSelector.exchangeRate);

  const paymentAccounts = useSelector(userPaymentAccountsSelector.all);

  const selectedCurrency = watch('currency');

  const currentBalance = useMemo(() => {
    return isMultiCurrencyEnabled
      ? paymentAccounts.find((account) => account?.currency === selectedCurrency)?.balance ?? 0
      : balance;
  }, [balance, selectedCurrency, isMultiCurrencyEnabled, paymentAccounts]);

  const amountOptions = useMemo(() => {
    return getWithdrawAmountOptions(
      preselectedAmounts,
      currentBalance,
      exchangeRate,
      selectedCurrency,
      isDisabled
    );
  }, [preselectedAmounts, currentBalance, exchangeRate, selectedCurrency, isDisabled]);

  const currencyOptions = Object.keys(minAmounts).map((currency) => ({
    value: currency,
    label: currency,
  }));

  return (
    <FormProvider {...form}>
      {isMultiCurrencyEnabled ? (
        <>
          <Box mb={1}>
            <MinWithdrawAmountTip amounts={minAmounts} />
          </Box>
          {currencyOptions.length > 1 ? (
            <Box mb={1} display="flex" alignItems="center">
              <Typography mr={1}>Currency</Typography>
              <Box flex={1}>
                <Controller
                  control={control}
                  name="currency"
                  render={({ field }) => (
                    <SelectInput
                      options={currencyOptions}
                      {...field}
                      disabled={isDisabled}
                      onChange={(e) => {
                        field.onChange(e);
                        trigger('amount');
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          ) : null}
        </>
      ) : null}
      <Box mb={1}>
        <Controller
          control={control}
          name="amount"
          render={({ field: { onChange, value, onBlur, ...rest } }) => (
            <OptionsWithInput
              value={value}
              options={amountOptions}
              onSelect={(newValue: number) => {
                onChange(newValue);
                onBlur?.();
              }}
              isInputDisabled={isDisabled}
              areOptionsDisabled={isDisabled}
              inputSuffix={` ${selectedCurrency}`}
              onBlur={(newValue: number) => {
                onChange(newValue);
                onBlur?.();
              }}
              {...rest}
            />
          )}
        />
      </Box>
      <Box mb={1}>
        {errors.amount?.message && (
          <ErrorMessage error={errors.amount.message} withSidePadding={false} />
        )}
      </Box>
    </FormProvider>
  );
};
